import Axios from "./Axios";
import { getBaseUrl } from "../constants";
import {
  PopularKeywordsRequestProps,
  KeywordTemplatesRequestProps,
} from "./types";

const popularKeywordsUrl = "/v1/templates/keywords/popular";
const keywordTemplatesUrl = "/v1/templates/keywords/templates";

/**
 * undefined 값은 쿼리스트링에서 뺀다. Axios 래퍼는 JSON 직렬화로 undefined 를
 * 걸러내지만, 명시적으로 걸러 두면 의도가 드러난다.
 */
const compact = (data: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== undefined && v !== "")
  );

/**
 * [GET] templates/keywords/popular
 * 특정 국가에서 게시된 템플릿의 설명(#태그)에서 추출한 해시태그 사용 순위.
 *
 * @param country : ISO 3166-1 alpha-2 국가 코드 (필수)
 * @param matchType : tag(해시태그 순위, 기본) | text(설명 본문 단어 순위)
 * @param startedAt : 게시일 조회 시작 (RFC3339). 없으면 endedAt 기준 30일 전
 * @param endedAt : 게시일 조회 끝 (RFC3339). 없으면 UTC now. 범위 최대 90일
 * @param limit : 반환 키워드 수 1~100 (기본 20)
 * @param templatesPerKeyword : 키워드별 대표 템플릿 수 1~50 (기본 10)
 * @param sortBy : 키워드 안 템플릿 정렬 like|download|share|view|aiScore (기본 view)
 */
export const getPopularKeywords = ({
  country,
  matchType,
  startedAt,
  endedAt,
  limit,
  templatesPerKeyword,
  sortBy,
}: PopularKeywordsRequestProps) => {
  const method = "GET";
  // 리전 규칙: 베이스 URL 은 호출 시점에 계산한다 (모듈 상수로 고정하지 않음).
  const url = `${getBaseUrl()}${popularKeywordsUrl}`;
  const data = compact({
    country,
    matchType,
    startedAt,
    endedAt,
    limit,
    templatesPerKeyword,
    sortBy,
  });

  return Axios(method, url, data);
};

/**
 * [GET] templates/keywords/templates
 * 특정 국가·기간에서 특정 해시태그가 붙은 템플릿의 전체 수와 페이지 목록.
 *
 * @param country : ISO 3166-1 alpha-2 국가 코드 (필수)
 * @param keyword : 키워드 (필수, 서버에서 소문자·# → 공백·공백 정리로 정규화)
 * @param matchType : tag(해시태그 정확 일치, 기본) | text(본문 단어 검색)
 * @param startedAt / endedAt : popular 와 동일
 * @param page : 1~10000 (기본 1)
 * @param display : 1~100 (기본 20)
 * @param sortBy : like|download|share|view|aiScore (기본 view)
 */
export const getKeywordTemplates = ({
  country,
  keyword,
  matchType,
  startedAt,
  endedAt,
  page,
  display,
  sortBy,
}: KeywordTemplatesRequestProps) => {
  const method = "GET";
  const url = `${getBaseUrl()}${keywordTemplatesUrl}`;
  const data = compact({
    country,
    keyword,
    matchType,
    startedAt,
    endedAt,
    page,
    display,
    sortBy,
  });

  return Axios(method, url, data);
};
